import { Observable, fromEvent, Subscription, switchMap, map } from 'rxjs';
import { UntypedFormGroup, UntypedFormControl, AbstractControl, UntypedFormArray, FormGroup, FormArray, FormControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseComponent } from '../components/base.component';

export default class FormHelper {
  static getDirtyControlNames(formGroup: UntypedFormGroup): string[] {
    const changedProperties: any = [];

    Object.keys(formGroup.controls).forEach((name) => {
      const currentControl = formGroup.controls[name];

      if (currentControl.dirty) {
        changedProperties.push(name);
      }
    });

    return changedProperties;
  }

  static getDirtyFormControls(formGroup: UntypedFormGroup): UntypedFormControl[] {
    const changedProperties: any = [];

    Object.keys(formGroup.controls).forEach((name) => {
      const control = formGroup.controls[name];

      if (control.dirty) {
        changedProperties.push(control);
      }
    });

    return changedProperties;
  }

  static isOnlyDirtyFormControl(formGroup: UntypedFormGroup, formControl: AbstractControl): boolean {
    const dirtyControls = this.getDirtyFormControls(formGroup);

    return dirtyControls.length === 1 && dirtyControls[0] === formControl;
  }

  static dirtyCheckBeforeUnload(observable: Observable<boolean>, component: BaseComponent): Subscription {
    return fromEvent(window, 'beforeunload')
      .unsubscribeOnDestroy(component)
      .subscribe(event => {
        const subscription = observable.subscribe(value => {
          if (value) {
            event.returnValue = false;
          }
        });

        subscription.unsubscribe();
      });
  }

  static dirtyCheckMultipleFormGroups(formGroups: () => FormGroup[]): boolean {
    return formGroups().some(formGroup => formGroup?.dirty);
  }

  static redirectAndReload(router: Router, uri: string): any {
    return router.navigateByUrl('/reload', { skipLocationChange: true }).then(() =>
      router.navigate([uri]));
  }

  static reloadSelf(router: Router): any {
    return this.redirectAndReload(router, router.url);
  }

  // Focusses the first invalid control
  static focusFirstInvalidFormControl(document: Document): Element | any {
    // Add more selectors here
    let elements = Array.from(document.querySelectorAll('mat-select.ng-invalid, input.ng-invalid, textarea.ng-invalid, mat-error'));

    if (elements.length) {
      // Prefer controls over error messages
      const invalidControls = elements.filter(control => control.tagName !== 'MAT-ERROR');

      if (invalidControls.length) {
        elements = invalidControls;
      }

      const invalidControl = elements[0];

      if (invalidControl.tagName === 'MAT-ERROR') {
        invalidControl.scrollIntoView();
      }
      else {
        (invalidControl as any).focus();
      }

      return invalidControl;
    }
  }

  static selectFirstInvalidFormArrayFormGroup<TFormGroup extends AbstractControl<any, any>>(formArray: FormArray<TFormGroup>): TFormGroup | null {
    for (let formGroup of formArray.controls) {
      if (formGroup.invalid) {
        return formGroup as TFormGroup;
      }
    }

    return null;
  }

  // Get all distinct validation error names
  static getDistinctValidatonErrorNames(control: AbstractControl): Set<string> {
    let errors: string[] = [];

    if (control instanceof FormControl) {
      // Return FormControl errors or null
      errors = [...(control.errors ? Object.keys(control.errors) : [])];
    }
    else if (control instanceof FormGroup || control instanceof FormArray) {
      errors = [...(control.errors ? Object.keys(control.errors) : [])];

      Object.keys(control.controls).forEach(key => {
        const childControl = control.get(key);

        if (childControl) {
          // Recursive call of the FormGroup fields
          const childErrors = this.getDistinctValidatonErrorNames(childControl);

          if (childErrors) {
            // Only add error if not null
            errors = [...childErrors];
          }
        }
      });
    }

    return new Set(errors);
  }
}
